import { AmendmentItems, AmendmentVersionGroup } from '../interfaces/AmendmentVersionGroup';
import { AmendmentAdditionalInformation, AmendmentChanges } from '../models/mongoose-generated-types';

interface AmendmentDetailLike {
  version?: number;
  additionalInformation?: Pick<AmendmentAdditionalInformation, 'description' | 'pointer' | 'header'>[];
  changes?: Pick<AmendmentChanges, 'reason' | 'pointer' | 'header'>[];
}

interface AmendmentEntry {
  sequence: number;
  amendments: AmendmentDetailLike[];
}

const emptyItems = (): AmendmentItems => ({ additionalInformation: [], changes: [] });

const getVersionNumbers = (entries: AmendmentEntry[]): number[] => {
  const versions = new Set<number>();
  for (const entry of entries) {
    for (const detail of entry.amendments) {
      versions.add(detail.version ?? 0);
    }
  }
  return [...versions].sort((a, b) => b - a);
};

// this entry's own (non-header) items for one version, pushed into `header` as a side effect when header-flagged
const buildSequenceItems = (entry: AmendmentEntry, version: number, header: AmendmentItems): (AmendmentItems & { sequence: number }) | null => {
  const sequenceItems = emptyItems();
  const matchingDetails = entry.amendments.filter((detail) => (detail.version ?? 0) === version);

  for (const detail of matchingDetails) {
    for (const info of detail.additionalInformation ?? []) {
      (info.header ? header : sequenceItems).additionalInformation.push({ description: info.description, pointer: info.pointer });
    }
    for (const change of detail.changes ?? []) {
      (change.header ? header : sequenceItems).changes.push({ reason: change.reason, pointer: change.pointer });
    }
  }

  const hasItems = sequenceItems.additionalInformation.length > 0 || sequenceItems.changes.length > 0;
  return hasItems ? { sequence: entry.sequence, ...sequenceItems } : null;
};

// for one version number: split matching details into the header bucket vs. one bucket per sequence
const buildVersion = (version: number, entries: AmendmentEntry[]): AmendmentVersionGroup => {
  const header = emptyItems();

  const items = entries
    .map((entry) => buildSequenceItems(entry, version, header))
    .filter((item): item is AmendmentItems & { sequence: number } => item !== null)
    .sort((a, b) => a.sequence - b.sequence);

  return { version, header, items };
};

export const buildAmendmentVersions = (entries: AmendmentEntry[]): AmendmentVersionGroup[] => {
  const versionNumbers = getVersionNumbers(entries);
  return versionNumbers.map((version) => buildVersion(version, entries));
};
